import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApplicantDetails } from '@/api/schema';
import { Panel } from '@/components/layout/Page';
import { ErrorState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { useApplications } from '../api';
import { APPLICANT_DETAIL_FIELDS } from '../applicationFields';
import styles from './ApplicationsPanel.module.css';

function Background({ details }: { details: ApplicantDetails | undefined }) {
  return (
    <dl className={styles.background}>
      {APPLICANT_DETAIL_FIELDS.map(({ name, label, type }) => {
        const value = details?.[name];
        return value === undefined || value === null || value === '' ? null : (
          <div key={name}>
            <dt>{label}</dt>
            <dd>
              {value}
              {type === 'number' ? ' years' : ''}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function ApplicationsPanel({ candidateId }: { candidateId?: string }) {
  const [page, setPage] = useState(1);
  const applications = useApplications(page, candidateId);
  return (
    <Panel>
      <h2>Website applications</h2>
      {applications.isPending ? (
        <Spinner label="Loading applications" />
      ) : applications.isError ? (
        <ErrorState
          message="Applications could not be loaded."
          onRetry={() => {
            void applications.refetch();
          }}
        />
      ) : applications.data.total === 0 ? (
        <p>
          No applications received
          {candidateId
            ? ' for this candidate'
            : ' yet. Submissions from the public Careers page appear here'}
          .
        </p>
      ) : (
        <>
          {candidateId ? (
            <div className={styles.applications}>
              {applications.data.items.map((item) => (
                <article
                  key={item.id}
                  className={styles.application}
                  aria-label={`Application for ${item.job_title}`}
                >
                  <h3>{item.job_title}</h3>
                  <p className={styles.date}>
                    Received {new Date(item.created_at).toLocaleDateString()}
                  </p>
                  {item.message ? <p className={styles.message}>{item.message}</p> : null}
                  <Background details={item.details} />
                </article>
              ))}
            </div>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <Tr>
                    <Th label="Candidate" />
                    <Th label="Job applied for" />
                    <Th label="Received" />
                    <Th label="Message" />
                    <Th label="Background" />
                  </Tr>
                </thead>
                <tbody>
                  {applications.data.items.map((item) => (
                    <Tr key={item.id}>
                      <Td>
                        <Link to={`/candidates/${item.candidate_id}`}>{item.full_name}</Link>
                      </Td>
                      <Td>{item.job_title}</Td>
                      <Td>{new Date(item.created_at).toLocaleDateString()}</Td>
                      <Td>{item.message || '—'}</Td>
                      <Td>
                        {APPLICANT_DETAIL_FIELDS.some(
                          ({ name }) =>
                            item.details?.[name] !== undefined &&
                            item.details[name] !== null &&
                            item.details[name] !== '',
                        ) ? (
                          <details className={styles.details}>
                            <summary>Application details</summary>
                            <Background details={item.details} />
                          </details>
                        ) : (
                          '—'
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
          <Pagination
            page={page}
            perPage={20}
            total={applications.data.total}
            label="applications"
            onChange={setPage}
          />
        </>
      )}
    </Panel>
  );
}
